import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ParseKeys } from "i18next";

import type { IAppointmentTableEndpoint } from "@api/appointment";
import { PageTable } from "@components/organisms/pageTable";
import {
  AppointmentCard,
  useAppointmentConverter,
} from "@components/organisms/appointmentCard";
import type { ITable } from "@/interfaces";
import type {
  IAppointmentTableExtraProps,
  IAppointmentTableType,
} from "@/templates/appointmentManagement";
import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icons";
import { path } from "@/routes/path";
import { useUserStore } from "@/stores/userStore";

const getTableTitle = (type: IAppointmentTableType): ParseKeys<"common"> => {
  if (type === "no-show") return "past_visit_list";
  if (type === "online-visit") return "onlineAppointmentTable";
  if (type === "in-person-visit") return "inPersonAppointmentTable";
  return "appointmentTableTitle";
};

export function AppointmentTable(
  props: ITable<IAppointmentTableEndpoint> & IAppointmentTableExtraProps,
) {
  const { t } = useTranslation("common");
  const {
    type,
    onPrint,
    onDownload,
    data,
    isLoading,
    isFetching,
    isError,
    tableParamProps,
    total,
  } = props;

  const role = useUserStore((state) => state.role);

  const { mapAppointmentToAppointmentList } = useAppointmentConverter();

  const navigate = useNavigate();

  const title = t(getTableTitle(type));

  return (
    <PageTable
      type={"row"}
      title={title}
      total={total}
      isFetching={isFetching}
      isLoading={isLoading}
      isError={isError}
      onDownload={onDownload}
      onPrint={onPrint}
      placeholder={"visitList"}
      actions={
        // TODO: make this button component
        <>
          {["staff", "patient"]?.includes(role || "") &&
            type !== "all" &&
            type !== "no-show" && (
              <Button
                mode={"main"}
                label={t("make_appointment")}
                icon={<Icon fontSize={"xl"} name={"AddSquare"} />}
                onClick={() => navigate(path.appointment.create.link(type))}
              />
            )}
        </>
      }
      {...tableParamProps}
    >
      {data?.map((item) => {
        const appointmentCardProps = mapAppointmentToAppointmentList(item);

        appointmentCardProps.actions.onEditIntakeForm = () => {
          if (appointmentCardProps.actions.canEditIntakeForm)
            navigate(path.intakeForm.edit.link(String(item.id)));
          else if (appointmentCardProps.actions.canViewIntakeForm)
            navigate(path.intakeForm.view.link(String(item.id)));
        };
        appointmentCardProps.actions.onView = () => {
          navigate(
            path.storedResult.viewAppointment.link({
              appointment_id: item.id,
              patient_id: item?.patient?.id,
              patient_type:
                item?.visit_for?.key === "my_self"
                  ? "patient"
                  : "family_member",
            }),
          );
        };
        appointmentCardProps.actions.onReschedule = () => {
          navigate(path.appointment.reschedule.link(String(item.id), type));
        };

        if (type === "no-show")
          return (
            <AppointmentCard
              key={item?.id}
              {...appointmentCardProps}
              variant={"no-show"}
              noShowCause={item?.no_show_cause?.key}
              noShowType={props.noShowType}
              noShowReason={item?.no_show_cause?.value}
            />
          );

        return (
          <AppointmentCard
            key={item?.id}
            {...appointmentCardProps}
            noShowCause={item?.no_show_cause?.key}
            noShowReason={item?.no_show_cause?.value}
          />
        );
      })}
    </PageTable>
  );
}
